import { useMemo } from 'react';
import { Patient } from '../../../types/types';
import { UseDurationScheduleReturn } from './useDurationSchedule';
import { UseFormsAndRemindersReturn } from './useFormsAndReminders';

interface UseAppointmentFormValidationProps {
  selectedPatient?: Patient;
  selectedAppointmentType: UseFormsAndRemindersReturn['selectedAppointmentType'];
  durationSchedule: UseDurationScheduleReturn;
  mode?: 'create' | 'edit';
}

export interface UseAppointmentFormValidationReturn {
  isFormValid: boolean;
  validationErrors: {
    patient?: string;
    appointmentType?: string;
    date?: string;
    duration?: string;
    time?: string;
  };
  getValidationMessage: () => string;
}

/**
 * Hook to handle form validation for appointment creation/editing
 */
export const useAppointmentFormValidation = ({
  selectedPatient,
  selectedAppointmentType,
  durationSchedule,
  mode = 'create',
}: UseAppointmentFormValidationProps): UseAppointmentFormValidationReturn => {
  const validationResult = useMemo(() => {
    const errors: UseAppointmentFormValidationReturn['validationErrors'] = {};

    // Patient validation (required for create mode)
    if (mode === 'create' && !selectedPatient?.patient_id) {
      errors.patient = 'Patient is required';
    }

    // Appointment type validation
    if (!selectedAppointmentType?.encounter_type) {
      errors.appointmentType = 'Appointment type is required';
    }

    // Date validation
    if (!durationSchedule.date) {
      errors.date = 'Date is required';
    }

    // Duration/time validation
    const { startDateTime, duration } = durationSchedule.getAppointmentDateTime();
    if (!startDateTime) {
      errors.time = 'Start time is required';
    }
    if (!duration) {
      errors.duration = 'Duration is required';
    }

    // Custom duration validation
    if (
      durationSchedule.isCustomDuration &&
      (!durationSchedule.startTime || !durationSchedule.endTime)
    ) {
      errors.time = 'Both start and end times are required for custom duration';
    }

    // Non-custom duration validation
    if (!durationSchedule.isCustomDuration && !durationSchedule.selectedDuration) {
      errors.duration = 'Duration must be selected';
    }

    const isFormValid = Object.keys(errors).length === 0;

    const getValidationMessage = (): string => {
      if (isFormValid) return '';

      const errorMessages = Object.values(errors).filter(Boolean);
      if (errorMessages.length === 1) {
        return errorMessages[0];
      }
      return 'Please fill all required fields';
    };

    return {
      isFormValid,
      validationErrors: errors,
      getValidationMessage,
    };
  }, [
    selectedPatient,
    selectedAppointmentType,
    durationSchedule.date,
    durationSchedule.startTime,
    durationSchedule.endTime,
    durationSchedule.selectedDuration,
    durationSchedule.isCustomDuration,
    durationSchedule.getAppointmentDateTime,
    mode,
  ]);

  return validationResult;
};
